import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { Form, FormControlProps } from 'react-bootstrap';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { ContentContext } from '../Context/ContentContext';
import { CouponContext } from '../Context/CouponContext';
import { OrderBumpContext } from '../Context/OrderBumpContext';
import { PlanContext } from '../Context/PlanContext';
import { useInstallmentTax } from '../hooks/useInstallmentTax';

interface IProps extends FormControlProps {
	customPrice?: number;
	maxInstallment?: number;
	setValue: (value: number) => void;
}

const InstallmentsInput = forwardRef(
	({ customPrice, maxInstallment, setValue, ...props }: IProps, ref) => {
		const [hasTaxes, setHasTaxes] = useState<boolean>(true);

		const { orderBumps } = useContext(OrderBumpContext);
		const bumpsTotal = orderBumps.reduce(
			(sum, bump) => sum + (bump.content.pricing?.price || 0),
			0
		);
		const { finance } = useContext(ApplicationContext);

		const { plan } = useContext(PlanContext);
		const { currentPrice, content } = useContext(ContentContext);
		const { coupon } = useContext(CouponContext);

		const { calculate, installments } = useInstallmentTax();

		useEffect(() => {
			const pricingSchema = plan
				? plan?.pricingSchema
				: content?.pricing.pricingSchema;

			const isAnticipationEnabled =
				finance?.isAnticipationEnabled ?? true;
			const anticipationResponsible =
				pricingSchema?.anticipationResponsible ?? 'CUSTOMER';

			const hasTaxes =
				!content?.pricing.isSmartInstallmentEnabled &&
				isAnticipationEnabled &&
				anticipationResponsible === 'CUSTOMER';

			setHasTaxes(hasTaxes);
		}, [plan, content, finance]);

		useEffect(() => {
			if (!currentPrice && customPrice) return;

			const isSmartInstallment =
				content?.pricing.isSmartInstallmentEnabled;

			calculate(
				(customPrice || plan?.price || currentPrice || 0) +
					bumpsTotal +
					(isSmartInstallment ? coupon?.flatDiscount || 0 : 0),
				maxInstallment || 12,
				hasTaxes,
				coupon?.flatDiscount || 0
			);
		}, [currentPrice, customPrice, plan, hasTaxes, orderBumps, coupon]);

		// Padrão = MENOR parcelamento disponível (1x), não o maior.
		//
		// Antes era `installments.length`: o comprador já encontrava o número
		// MÁXIMO de parcelas selecionado ao abrir o formulário — e, com os juros
		// de antecipação por conta dele (`hasTaxes` acima), pagava o total mais
		// caro sem ter escolhido nada. Agora o padrão é o mais barato, e
		// parcelar passa a ser um ato deliberado do comprador.
		//
		// É a PRIMEIRA opção da lista, e não o literal `1`, porque
		// `useInstallmentTax` filtra por `installmentsSchema.isActive`: se o
		// lojista desativar a 1x, ela não existe na lista, e fixar `1`
		// selecionaria uma opção ausente. A lista é montada em ordem crescente
		// (`for (let i = 1; …)`), então a primeira é sempre a menor disponível.
		const parcelaPadrao = installments[0]?.number;

		useEffect(() => {
			if (!parcelaPadrao) return;

			setValue(parcelaPadrao);
		}, [installments]);

		return (
			<Form.Control as="select" {...props} ref={ref} size="lg">
				{installments.map(({ number, price, label }) => (
					<option
						key={number}
						value={number}
						selected={number === parcelaPadrao}
					>
						{label || `${number}x de ${formatCurrency(price)}`}
					</option>
				))}
			</Form.Control>
		);
	}
);

InstallmentsInput.displayName = 'InstallmentsInput';

export { InstallmentsInput };
